import "./navigation.css";

import CachedIcon from "@mui/icons-material/Cached";
import HomeIcon from "@mui/icons-material/Home";
import MenuIcon from "@mui/icons-material/Menu";
import AppBar from "@mui/material/AppBar";
import Button from "@mui/material/Button";
import MUIIconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import React from "react";
import { BrowserView, isMobile, MobileView } from "react-device-detect";
import { useTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MasterIndex } from "verdi-web-client";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";
import { IconButton } from "@/components/deprecated-mui/icon-button";
import { LanguageSelect } from "@/components/language-select";
import { Z_INDEX_VALUES } from "@/configs/z-index";
import { OrganizationLockoutBanner } from "@/features/lockout-banner/organization-lockout-banner";
import { ProductUpdateButton } from "@/features/product-update/product-update-button";
import { NAMESPACES } from "@/i18n/constants";
import FocusContext from "../../services/map-management/focus-context";
import GlobalOptions from "../../utils/global-options";
import ReactComponentBase from "../../utils/react-component-base";
import { Document360ChatbotButton } from "../document360-chatbot/document360-chatbot-button";
import { AccountSelect } from "./account-select";
import { Drawer } from "./drawer/drawer";
import { SettingsMenu } from "./settings-menu";

function HomeButton() {
    const { t } = useTranslation(NAMESPACES.FEATURES.NAVIGATION, { keyPrefix: "nav-bar" });

    return <IconButton component={Link} to={`/`} title={t("home")} icon={<HomeIcon color={"gray"} />} />;
}

function RefreshButton() {
    const { t } = useTranslation(NAMESPACES.FEATURES.NAVIGATION, { keyPrefix: "nav-bar" });
    return (
        <IconButton
            onClick={async () => {
                window.location.reload();
            }}
            title={t("refresh")}
            icon={<CachedIcon />}
        />
    );
}

class NavBar extends ReactComponentBase {
    constructor(props) {
        super(props);

        this.state = {
            menuAnchor: null,
            mobileMenuAnchor: null,
            admin: SessionHandler.admin,
            loggedIn: SessionHandler.loggedIn,
            selectedAccount: "",
            selectedAOI: "",
        };

        SessionHandler.onLogIn.addListener(() => {
            this.setState({
                accounts: SessionHandler.accounts,
                admin: SessionHandler.admin,
                loggedIn: SessionHandler.loggedIn,
            });
        });

        SessionHandler.onLogOut.addListener(() => {
            this.setState({
                accounts: SessionHandler.accounts,
                admin: SessionHandler.admin,
                loggedIn: SessionHandler.loggedIn,
            });
        });

        sortAccounts();

        SessionHandler.onSessionLoad.addListener(() => {
            sortAccounts();
            setTimeout(() => {
                if (
                    SessionHandler.currentUserObject &&
                    SessionHandler.currentUserObject.dbID !== this.state.selectedAccount
                ) {
                    this.setState({
                        selectedAccount: SessionHandler.currentUserObject.dbID,
                    });
                } else {
                    this.forceUpdate();
                }
            }, 100);
        });

        FocusContext.onAOIChange.addListener(() => {
            if (FocusContext.selectedAOI !== this.state.selectedAOI) {
                this.setState({
                    selectedAOI: FocusContext.selectedAOI,
                });
            } else {
                this.forceUpdate();
            }
        });
    }

    componentDidMount() {
        // Pages, dialogs and the map offset themselves by --nav-bar-height, which has to cover the lockout banner.
        this.navBarResizeObserver = new ResizeObserver(() => {
            document.body.style.setProperty("--nav-bar-height", `${this.ownComponent.offsetHeight}px`);
        });
        this.navBarResizeObserver.observe(this.ownComponent);
    }

    componentWillUnmount() {
        this.navBarResizeObserver.disconnect();
    }

    toggleMobileMenu = (e) => {
        this.setState((prevState) => ({ mobileMenuAnchor: prevState.mobileMenuAnchor ? null : e.target }));
    };

    render() {
        sortAccounts();
        const multipleAreasOfInterest = MasterIndex.aoi.all.length > 1;

        // Get navbar height from css variable
        const root = document.querySelector("#root");
        const styles = getComputedStyle(root);
        const navbarHeight = parseInt(styles.getPropertyValue("--nav-toolbar-height"), 10);

        return (
            <>
                <AppBar position={"static"} style={{ zIndex: 10 }}>
                    <Toolbar style={{ minHeight: "var(--nav-bar-height, 55px)" }} />
                </AppBar>
                <AppBar position={"fixed"} style={{ zIndex: Z_INDEX_VALUES.NAV_BAR }} id={this.uid} elevation={0}>
                    <Toolbar style={{ minHeight: "var(--nav-toolbar-height, 55px)" }}>
                        {this.state.loggedIn && (
                            <MobileView style={{ display: "flex" }}>
                                <MUIIconButton
                                    edge={"start"}
                                    color={"inherit"}
                                    aria-label={"menu"}
                                    onClick={this.toggleMobileMenu}
                                    size={"large"}
                                >
                                    <MenuIcon />
                                </MUIIconButton>

                                <Drawer
                                    multipleAOI={multipleAreasOfInterest}
                                    open={Boolean(this.state.mobileMenuAnchor)}
                                    onClose={() => {
                                        this.setState({ mobileMenuAnchor: null });
                                    }}
                                    admin={this.state.admin}
                                    loggedIn={this.state.loggedIn}
                                    mobileMenuAnchorSetter={(anchor) => {
                                        this.setState({ mobileMenuAnchor: anchor });
                                    }}
                                />
                            </MobileView>
                        )}
                        <Link to={"/"}>
                            <img
                                style={{
                                    minHeight: isMobile ? "30px" : "25px",
                                }}
                                src={
                                    "https://verdi-web-assets.s3.us-east-2.amazonaws.com/logo/PNG/white_wordmark@0.5x.png"
                                }
                                id={"main-logo"}
                            />
                        </Link>
                        <div style={{ flexGrow: "10" }} />
                        {this.state.loggedIn && (
                            <MobileView>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <RefreshButton />
                                    <Document360ChatbotButton />
                                    <HomeButton />
                                </div>
                            </MobileView>
                        )}

                        <BrowserView className={"MainBrowserToolbar"} style={{ display: "flex" }}>
                            {this.state.loggedIn ? (
                                <>
                                    <AccountSelect />
                                    <RefreshButton />
                                    <HomeButton />
                                    <Document360ChatbotButton />
                                    <ProductUpdateButton />
                                    <SettingsMenu navBarHeight={navbarHeight} />
                                    {GlobalOptions.enableLanguageSelector && <LanguageSelect />}
                                </>
                            ) : (
                                <>
                                    <Button
                                        color={"inherit"}
                                        onClick={() => {
                                            window.open("https://www.verdi.ag/about", "_blank");
                                        }}
                                    >
                                        {this.props.t("nav-bar.about")}
                                    </Button>
                                    <Button
                                        color={"inherit"}
                                        onClick={() => {
                                            window.open("https://www.verdi.ag/terms", "_blank");
                                        }}
                                    >
                                        {this.props.t("nav-bar.terms-and-conditions")}
                                    </Button>
                                    {GlobalOptions.enableLanguageSelector && <LanguageSelect />}
                                </>
                            )}
                        </BrowserView>
                    </Toolbar>
                    <OrganizationLockoutBanner />
                </AppBar>
            </>
        );
    }
}

export default withTranslation(NAMESPACES.FEATURES.NAVIGATION)(NavBar);

function sortAccounts() {
    if (!SessionHandler.accounts) {
        return;
    }

    SessionHandler.accounts.sort((a, b) => {
        if (a.name && b.name) {
            return a.name.localeCompare(b.name);
        }
        if (a.name) {
            return 1;
        }
        return -1;
    });
}
